import { useEffect, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { foldSearch, rowsOf } from '@eis/core/reporting';
import { usePermissions } from '@eis/patterns';
import { displayFieldName } from '@eis/patterns/reporting';
import { reportingPermissions as p } from '../../../permissions';
import { codeMessage } from '../../../lib/codes';
import { useQueryPolling, useSampleDataset } from '../api/datasets';

/** The rows a source's preview reads (the service's maximum, SampleDatasetRequest.limit). */
export const PREVIEW_ROWS = 100;

/**
 * The first rows of a catalog dataset, read when it is chosen as a custom dataset's source (owner
 * feedback 2026-11-01: "see what is in it before joining it"). One sample query of
 * {@link PREVIEW_ROWS} rows per dataset chosen — on Athena a `LIMIT 100` over the `_mat` twin —
 * shown as the warehouse answers them, every column, scrolling both ways. Without
 * `reporting.queries.run` there is no preview, only the choice.
 */
export function DatasetPreview({ name }: { readonly name: string }): ReactNode {
  const { t } = useTranslation('reporting');
  const permissions = usePermissions();
  const sample = useSampleDataset();
  // The query of THIS dataset: a run started for the previous choice is never shown for this one.
  const [run, setRun] = useState<{ name: string; id?: string } | undefined>(undefined);
  const queryId = run?.name === name ? run.id : undefined;
  const polling = useQueryPolling(queryId);
  const canRun = permissions.can(p.queries.run);

  useEffect(() => {
    if (!canRun || name === '') return;
    sample.mutate(
      { name, limit: PREVIEW_ROWS },
      {
        onSuccess: (q) => {
          setRun({ name, ...(q.id === undefined ? {} : { id: q.id }) });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, canRun]);

  if (!canRun || name === '') return null;
  const query = polling.query;
  const failure = query?.error?.code;
  if (sample.isError || failure !== undefined) {
    return (
      <Alert severity="warning" data-testid="dataset-preview">
        {codeMessage(t, failure).title}
      </Alert>
    );
  }
  if (query?.state !== 'SUCCEEDED') {
    return (
      <Box data-testid="dataset-preview" aria-busy>
        <Typography variant="caption" color="text.secondary">
          {t('datasets.preview.loading', { name })}
        </Typography>
        <LinearProgress aria-label={t('datasets.preview.loading', { name })} />
      </Box>
    );
  }
  const columns = query.columns ?? [];
  const rows = rowsOf(query.rows);
  return (
    <Box data-testid="dataset-preview">
      <Typography variant="caption" color="text.secondary">
        {rows.length === 0
          ? t('datasets.preview.empty', { name })
          : t('datasets.preview.rows', { count: rows.length, name })}
      </Typography>
      {rows.length > 0 && (
        <TableContainer
          sx={{ maxHeight: 240, border: 1, borderColor: 'divider', borderRadius: 1 }}
          tabIndex={0}
          aria-label={t('datasets.preview.label', { name })}
          role="region"
        >
          <Table size="small" stickyHeader aria-label={t('datasets.preview.label', { name })}>
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {displayFieldName(c.label ?? c.key ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, r) => (
                <TableRow key={r}>
                  {columns.map((c, i) => (
                    <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>
                      {row[i] ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export interface FieldPickerProps {
  readonly label: string;
  readonly fields: readonly string[];
  readonly value: string;
  readonly onChange: (field: string) => void;
  readonly disabled?: boolean;
  readonly helperText?: string;
}

/**
 * A field of a view, searchable (owner feedback 2026-11-01): a lake view has dozens to hundreds of
 * columns. Shown as the field list shows them ("Police Code"), matched on that and on the column
 * name, accent- and case-insensitively on every word.
 */
export function FieldPicker({
  label,
  fields,
  value,
  onChange,
  disabled = false,
  helperText,
}: FieldPickerProps): ReactNode {
  const { t } = useTranslation('reporting');
  return (
    <Autocomplete
      size="small"
      options={[...fields]}
      value={value === '' ? null : value}
      disabled={disabled}
      getOptionLabel={(f) => displayFieldName(f)}
      filterOptions={(list, state) => {
        const terms = foldSearch(state.inputValue.trim());
        if (terms.length === 0) return list;
        return list.filter((f) => {
          const text = foldSearch(`${f} ${displayFieldName(f)}`).join(' ');
          return terms.every((term) => text.includes(term));
        });
      }}
      noOptionsText={t('datasets.noFieldMatch')}
      onChange={(_, next) => {
        onChange(next ?? '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          {...(helperText === undefined ? {} : { helperText })}
        />
      )}
    />
  );
}
